const PARENT_VARIANT_IDS = new Set(['46355686228139', '46355685245099']);
const GIFT_VARIANT_ID = '46345688023211';
const GIFT_FLAG = '_bw_gift';
const GIFT_PARENT = '_bw_gift_for';
const EVENT_SOURCE = 'berkwellness-gift-bundle';

function normalizedId(value) {
  return String(value || '').trim();
}

function isManagedGift(item) {
  const properties = item && item.properties;
  return (
    normalizedId(item && item.variant_id) === GIFT_VARIANT_ID &&
    properties &&
    String(properties[GIFT_FLAG]) === 'true'
  );
}

export function planGiftReconciliation(cart) {
  const items = Array.isArray(cart && cart.items) ? cart.items : [];
  const parentQuantities = new Map();
  const managedByParent = new Map();
  const updates = {};
  const additions = [];

  for (const item of items) {
    const variantId = normalizedId(item.variant_id);
    if (PARENT_VARIANT_IDS.has(variantId) && !isManagedGift(item)) {
      parentQuantities.set(variantId, (parentQuantities.get(variantId) || 0) + Number(item.quantity || 0));
    }

    if (!isManagedGift(item)) continue;
    const parentId = normalizedId(item.properties && item.properties[GIFT_PARENT]);
    const managedItems = managedByParent.get(parentId) || [];
    managedItems.push(item);
    managedByParent.set(parentId, managedItems);
  }

  for (const [parentId, managedItems] of managedByParent) {
    const desiredQuantity = parentQuantities.get(parentId) || 0;
    const retainedItem = managedItems[0];

    if (!PARENT_VARIANT_IDS.has(parentId) || desiredQuantity === 0) {
      for (const item of managedItems) updates[item.key] = 0;
      continue;
    }

    if (Number(retainedItem.quantity || 0) !== desiredQuantity) {
      updates[retainedItem.key] = desiredQuantity;
    }

    for (const duplicate of managedItems.slice(1)) updates[duplicate.key] = 0;
  }

  for (const [parentId, quantity] of parentQuantities) {
    if (quantity < 1 || managedByParent.has(parentId)) continue;
    additions.push({
      id: Number(GIFT_VARIANT_ID),
      quantity,
      properties: {
        [GIFT_FLAG]: 'true',
        [GIFT_PARENT]: parentId,
      },
    });
  }

  return { updates, additions };
}

function jsonRequest(url, body) {
  return fetch(url, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  }).then(async (response) => {
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const message = payload && (payload.description || payload.message);
      throw new Error(message || 'Cart request failed');
    }
    return payload;
  });
}

function cartUrl() {
  const configured = typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url;
  const route = configured || '/cart';
  return route.endsWith('.js') ? route : `${route}.js`;
}

function cartUpdateUrl() {
  const configured = typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_update_url;
  return configured || '/cart/update.js';
}

function cartAddUrl() {
  const configured = typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url;
  return configured || '/cart/add.js';
}

let running = false;
let queued = false;
let timer;

async function reconcileGiftBundle() {
  if (running) {
    queued = true;
    return;
  }

  running = true;
  try {
    const cart = await jsonRequest(cartUrl());
    const plan = planGiftReconciliation(cart);
    const hasUpdates = Object.keys(plan.updates).length > 0;
    const hasAdditions = plan.additions.length > 0;

    if (!hasUpdates && !hasAdditions) return;
    if (hasUpdates) await jsonRequest(cartUpdateUrl(), { updates: plan.updates });
    if (hasAdditions) await jsonRequest(cartAddUrl(), { items: plan.additions });

    const reconciledCart = await jsonRequest(cartUrl());
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: reconciledCart,
          sourceId: EVENT_SOURCE,
          data: {
            source: EVENT_SOURCE,
            itemCount: reconciledCart.item_count,
          },
        },
      })
    );
  } catch (error) {
    console.error('BERKWELLNESS gift bundle reconciliation failed', error);
  } finally {
    running = false;
    if (queued) {
      queued = false;
      scheduleReconciliation();
    }
  }
}

function scheduleReconciliation() {
  window.clearTimeout(timer);
  timer = window.setTimeout(reconcileGiftBundle, 180);
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  document.addEventListener('cart:update', (event) => {
    if (event.detail && event.detail.sourceId === EVENT_SOURCE) return;
    scheduleReconciliation();
  });
  window.addEventListener('pageshow', scheduleReconciliation);
}
